import { Integration, IntegrationTool, PaginatedResponse } from '../types';

// Mock integrations data
const mockIntegrations: Integration[] = [
  {
    id: '1',
    name: 'Jira Integration',
    type: 'jira',
    status: 'connected',
    config: {
      url: 'https://company.atlassian.net',
      project_key: 'PROJ',
      username: 'api-user',
      last_sync_count: 145
    },
    created_at: '2024-01-10T09:00:00Z',
    updated_at: '2024-01-21T14:30:00Z',
    last_sync: '2024-01-21T14:30:00Z',
    error_message: null
  },
  {
    id: '2',
    name: 'GitHub Repository',
    type: 'github',
    status: 'connected',
    config: {
      repository: 'company/main-app',
      owner: 'company',
      branch: 'main',
      webhook_enabled: true,
      last_commit: 'abc123def'
    },
    created_at: '2024-01-12T11:20:00Z',
    updated_at: '2024-01-21T16:45:00Z',
    last_sync: '2024-01-21T16:45:00Z',
    error_message: null
  },
  {
    id: '3',
    name: 'Confluence Wiki',
    type: 'confluence',
    status: 'connected',
    config: {
      url: 'https://company.atlassian.net/wiki',
      space_key: 'DEV',
      last_page_count: 87
    },
    created_at: '2024-01-15T16:30:00Z',
    updated_at: '2024-01-20T10:15:00Z',
    last_sync: '2024-01-20T10:15:00Z',
    error_message: null
  },
  {
    id: '4',
    name: 'Slack Workspace',
    type: 'slack',
    status: 'error',
    config: {
      workspace: 'company-team',
      channel: '#dev-alerts',
      bot_token: 'xoxb-***'
    },
    created_at: '2024-01-18T14:00:00Z',
    updated_at: '2024-01-21T08:30:00Z',
    last_sync: '2024-01-19T12:00:00Z',
    error_message: 'Authentication failed: Token expired'
  },
  {
    id: '5',
    name: 'Custom API Service',
    type: 'custom',
    status: 'disconnected',
    config: {
      endpoint: 'https://api.internal.company.com/v1',
      auth_type: 'bearer',
      timeout: 30000
    },
    created_at: '2024-01-20T13:45:00Z',
    updated_at: '2024-01-21T09:20:00Z',
    last_sync: null,
    error_message: 'Connection timeout'
  },
  {
    id: '6',
    name: 'Development Database',
    type: 'custom',
    status: 'connected',
    config: {
      host: 'dev-db.company.com',
      database: 'app_development',
      schema: 'public',
      read_only: true
    },
    created_at: '2024-01-08T10:30:00Z',
    updated_at: '2024-01-21T12:00:00Z',
    last_sync: '2024-01-21T12:00:00Z',
    error_message: null
  }
];

// Mock integration tools
const mockTools: IntegrationTool[] = [
  // Jira Tools
  {
    name: 'create_jira_issue',
    description: 'Create a new issue in Jira with specified details',
    parameters: {
      project: { type: 'string', required: true, description: 'Project key' },
      summary: { type: 'string', required: true, description: 'Issue summary' },
      description: { type: 'string', required: false, description: 'Issue description' },
      issue_type: { type: 'string', required: true, description: 'Issue type (Bug, Story, Task)' },
      priority: { type: 'string', required: false, description: 'Issue priority' }
    },
    integration_type: 'jira'
  },
  {
    name: 'update_jira_issue',
    description: 'Update an existing Jira issue',
    parameters: {
      issue_key: { type: 'string', required: true, description: 'Issue key (e.g., PROJ-123)' },
      status: { type: 'string', required: false, description: 'New status' },
      assignee: { type: 'string', required: false, description: 'Assignee username' },
      comment: { type: 'string', required: false, description: 'Comment to add' }
    },
    integration_type: 'jira'
  },
  {
    name: 'search_jira_issues',
    description: 'Search for Jira issues using JQL',
    parameters: {
      jql: { type: 'string', required: true, description: 'JQL query string' },
      max_results: { type: 'number', required: false, description: 'Maximum number of results' }
    },
    integration_type: 'jira'
  },

  // GitHub Tools
  {
    name: 'create_github_pr',
    description: 'Create a pull request in GitHub',
    parameters: {
      title: { type: 'string', required: true, description: 'PR title' },
      body: { type: 'string', required: false, description: 'PR description' },
      head: { type: 'string', required: true, description: 'Source branch' },
      base: { type: 'string', required: true, description: 'Target branch' }
    },
    integration_type: 'github'
  },
  {
    name: 'get_github_file',
    description: 'Get file contents from GitHub repository',
    parameters: {
      path: { type: 'string', required: true, description: 'File path' },
      ref: { type: 'string', required: false, description: 'Branch or commit SHA' }
    },
    integration_type: 'github'
  },
  {
    name: 'create_github_issue',
    description: 'Create an issue in GitHub repository',
    parameters: {
      title: { type: 'string', required: true, description: 'Issue title' },
      body: { type: 'string', required: false, description: 'Issue description' },
      labels: { type: 'array', required: false, description: 'Issue labels' }
    },
    integration_type: 'github'
  },

  // Confluence Tools
  {
    name: 'create_confluence_page',
    description: 'Create a new page in Confluence',
    parameters: {
      title: { type: 'string', required: true, description: 'Page title' },
      content: { type: 'string', required: true, description: 'Page content (HTML)' },
      parent_id: { type: 'string', required: false, description: 'Parent page ID' }
    },
    integration_type: 'confluence'
  },
  {
    name: 'update_confluence_page',
    description: 'Update an existing Confluence page',
    parameters: {
      page_id: { type: 'string', required: true, description: 'Page ID' },
      title: { type: 'string', required: false, description: 'New page title' },
      content: { type: 'string', required: false, description: 'New page content (HTML)' }
    },
    integration_type: 'confluence'
  },
  {
    name: 'search_confluence_pages',
    description: 'Search for Confluence pages',
    parameters: {
      query: { type: 'string', required: true, description: 'Search query' },
      space: { type: 'string', required: false, description: 'Space key to search in' }
    },
    integration_type: 'confluence'
  },

  // Slack Tools
  {
    name: 'send_slack_message',
    description: 'Send a message to a Slack channel',
    parameters: {
      channel: { type: 'string', required: true, description: 'Channel name or ID' },
      text: { type: 'string', required: true, description: 'Message text' },
      thread_ts: { type: 'string', required: false, description: 'Thread timestamp for replies' }
    },
    integration_type: 'slack'
  },
  {
    name: 'get_slack_users',
    description: 'Get list of users in Slack workspace',
    parameters: {
      limit: { type: 'number', required: false, description: 'Maximum number of users to return' }
    },
    integration_type: 'slack'
  }
];

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const integrationsService = {
  async getIntegrations(page: number = 1, size: number = 20): Promise<PaginatedResponse<Integration>> {
    await delay(300);
    
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const items = mockIntegrations.slice(startIndex, endIndex);
    
    return {
      items,
      total: mockIntegrations.length,
      page,
      size,
      pages: Math.ceil(mockIntegrations.length / size)
    };
  },

  async getIntegration(id: string): Promise<Integration> {
    await delay(200);
    
    const integration = mockIntegrations.find(i => i.id === id);
    if (!integration) {
      throw new Error('Integration not found');
    }
    return integration;
  },

  async createIntegration(data: Partial<Integration>): Promise<Integration> {
    await delay(500);
    
    const newIntegration: Integration = {
      id: (mockIntegrations.length + 1).toString(),
      name: data.name || 'New Integration',
      type: data.type || 'custom',
      status: 'disconnected',
      config: data.config || {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_sync: null,
      error_message: null
    };
    
    mockIntegrations.push(newIntegration);
    return newIntegration;
  },

  async updateIntegration(id: string, data: Partial<Integration>): Promise<Integration> {
    await delay(400);
    
    const integrationIndex = mockIntegrations.findIndex(i => i.id === id);
    if (integrationIndex === -1) {
      throw new Error('Integration not found');
    }
    
    const updatedIntegration = {
      ...mockIntegrations[integrationIndex],
      ...data,
      updated_at: new Date().toISOString()
    };
    
    mockIntegrations[integrationIndex] = updatedIntegration;
    return updatedIntegration;
  },

  async deleteIntegration(id: string): Promise<void> {
    await delay(300);
    
    const integrationIndex = mockIntegrations.findIndex(i => i.id === id);
    if (integrationIndex === -1) {
      throw new Error('Integration not found');
    }
    
    mockIntegrations.splice(integrationIndex, 1);
  },

  async testIntegration(id: string): Promise<{ success: boolean; message: string; response_time?: number }> {
    await delay(1000 + Math.random() * 2000);
    
    const integration = mockIntegrations.find(i => i.id === id);
    if (!integration) {
      throw new Error('Integration not found');
    }
    
    // Simulate test results
    const success = Math.random() > 0.2; // 80% success rate
    const responseTime = Math.floor(100 + Math.random() * 500);
    
    return {
      success,
      message: success 
        ? `Connection successful! Response time: ${responseTime}ms`
        : 'Connection failed: Unable to authenticate with the service',
      response_time: success ? responseTime : undefined
    };
  },

  async syncIntegration(id: string): Promise<{ success: boolean; synced_items: number; message: string }> {
    await delay(2000 + Math.random() * 3000);
    
    const integration = mockIntegrations.find(i => i.id === id);
    if (!integration) {
      throw new Error('Integration not found');
    }
    
    if (integration.status !== 'connected') {
      return {
        success: false,
        synced_items: 0,
        message: 'Integration is not connected'
      };
    }
    
    const syncedItems = Math.floor(10 + Math.random() * 100);
    
    // Update last sync time
    const integrationIndex = mockIntegrations.findIndex(i => i.id === id);
    if (integrationIndex !== -1) {
      mockIntegrations[integrationIndex].last_sync = new Date().toISOString();
    }
    
    return {
      success: true,
      synced_items: syncedItems,
      message: `Successfully synced ${syncedItems} items`
    };
  },

  async getIntegrationTools(integrationType?: string): Promise<IntegrationTool[]> {
    await delay(200);
    
    if (integrationType) {
      return mockTools.filter(tool => tool.integration_type === integrationType);
    }
    
    return mockTools;
  },

  async getIntegrationTypes(): Promise<Array<{
    type: string;
    name: string;
    description: string;
    icon: string;
    features: string[];
    setup_complexity: 'easy' | 'medium' | 'advanced';
  }>> {
    await delay(200);
    
    return [
      {
        type: 'jira',
        name: 'Jira',
        description: 'Issue tracking and project management',
        icon: '🎯',
        features: ['Issue Management', 'Project Tracking', 'Workflow Automation', 'Reporting'],
        setup_complexity: 'easy'
      },
      {
        type: 'github',
        name: 'GitHub',
        description: 'Code repository and collaboration platform',
        icon: '🐙',
        features: ['Repository Access', 'Pull Requests', 'Issue Tracking', 'Webhooks'],
        setup_complexity: 'easy'
      },
      {
        type: 'confluence',
        name: 'Confluence',
        description: 'Team collaboration and documentation',
        icon: '📚',
        features: ['Page Management', 'Content Search', 'Space Organization', 'Templates'],
        setup_complexity: 'medium'
      },
      {
        type: 'slack',
        name: 'Slack',
        description: 'Team communication and messaging',
        icon: '💬',
        features: ['Messaging', 'Channel Management', 'User Directory', 'Bot Integration'],
        setup_complexity: 'easy'
      },
      {
        type: 'custom',
        name: 'Custom API',
        description: 'Custom REST API integration',
        icon: '🔌',
        features: ['HTTP Requests', 'Authentication', 'Custom Headers', 'Data Transformation'],
        setup_complexity: 'advanced'
      }
    ];
  },

  async getIntegrationStatistics(): Promise<{
    total_integrations: number;
    connected_integrations: number;
    failed_integrations: number;
    last_sync_count: number;
    available_tools: number;
  }> {
    await delay(300);
    
    const connectedIntegrations = mockIntegrations.filter(i => i.status === 'connected').length;
    const failedIntegrations = mockIntegrations.filter(i => i.status === 'error').length;
    const lastSyncCount = mockIntegrations
      .filter(i => i.last_sync)
      .reduce((sum, i) => sum + (i.config.last_sync_count || 0), 0);
    
    return {
      total_integrations: mockIntegrations.length,
      connected_integrations: connectedIntegrations,
      failed_integrations: failedIntegrations,
      last_sync_count: lastSyncCount,
      available_tools: mockTools.length
    };
  },

  async toggleIntegrationStatus(id: string, enabled: boolean): Promise<Integration> {
    await delay(300);
    
    const integrationIndex = mockIntegrations.findIndex(i => i.id === id);
    if (integrationIndex === -1) {
      throw new Error('Integration not found');
    }
    
    mockIntegrations[integrationIndex].status = enabled ? 'connected' : 'disconnected';
    mockIntegrations[integrationIndex].updated_at = new Date().toISOString();
    mockIntegrations[integrationIndex].error_message = enabled ? null : 'Manually disconnected';
    
    return mockIntegrations[integrationIndex];
  }
}; 